import type { Zone } from '@seek/audience-zones';

import { useCandidateId } from 'src/components/Profile/data';
import type { Maybe, Page } from 'src/types';
import { VerificationsTestVariant } from 'src/types/graphql';

import { useAppConfig } from '../appConfig';
import { useTypedSelector } from '../hooks/useTypedSelector';

import { isFeatureActive } from '.';

export type ExperimentVariant = '0' | '1' | '2';

const TEST_GROUP_SPLITTING_POINT = Math.floor(1000 / 3); // Split a third per test group

const EXPERIMENT_VARIANT_GENERIC_GROUP: ExperimentVariant = '0'; // Display generic Get Verified CTA button
const EXPERIMENT_VARIANT_WORKPLACE_GROUP: ExperimentVariant = '1'; // Prioritise workplace
const EXPERIMENT_VARIANT_IDENTITY_GROUP: ExperimentVariant = '2'; // Prioritise identity

const last3DigitsOfCandidateId = (candidateId: string) =>
  parseInt(candidateId, 10) % 1000;

const inWorkplaceGroup = (candidateId: string): boolean => {
  const isInTestGroup =
    last3DigitsOfCandidateId(candidateId) < TEST_GROUP_SPLITTING_POINT;

  return isInTestGroup;
};

const inIdentityGroup = (candidateId: string): boolean => {
  const isInTestGroup =
    last3DigitsOfCandidateId(candidateId) >= TEST_GROUP_SPLITTING_POINT &&
    last3DigitsOfCandidateId(candidateId) < TEST_GROUP_SPLITTING_POINT * 2;

  return isInTestGroup;
};

export const getVerificationsNudgeExperimentVariant = ({
  zone,
  page,
  candidateId,
}: {
  zone: Zone;
  page: Page;
  candidateId: Maybe<string>;
}): ExperimentVariant | undefined => {
  const isProfileAndANZ =
    page === 'profile-page' && (zone === 'anz-1' || zone === 'anz-2');

  const isVerificationsNudgeAbTestFeatureFlagActive = isFeatureActive(
    'verifications-nudge',
  ); // Used for default value in testing

  if (isProfileAndANZ && candidateId) {
    if (
      inWorkplaceGroup(candidateId) ||
      isVerificationsNudgeAbTestFeatureFlagActive
    ) {
      return EXPERIMENT_VARIANT_WORKPLACE_GROUP;
    }

    if (inIdentityGroup(candidateId)) {
      return EXPERIMENT_VARIANT_IDENTITY_GROUP;
    }

    return EXPERIMENT_VARIANT_GENERIC_GROUP;
  }
};

export const verifiableExperimentVariantToTestGroup: Record<
  '0' | '1' | '2',
  VerificationsTestVariant
> = {
  '0': VerificationsTestVariant.Opendetails,
  '1': VerificationsTestVariant.Verifiedworkemail,
  '2': VerificationsTestVariant.Identity,
};

export const useVerificationsNudgeAbTest = () => {
  const page = useTypedSelector((store) => store.router.page);
  const { zone } = useAppConfig();
  const { candidateId } = useCandidateId();

  const experimentVariant = getVerificationsNudgeExperimentVariant({
    zone,
    page,
    candidateId,
  });

  const testGroup = experimentVariant
    ? verifiableExperimentVariantToTestGroup[experimentVariant]
    : null;

  return { experimentVariant, testGroup };
};
